// Basic
import PropTypes from 'prop-types';
// Store & actions
import useSessionStore from '@/app/store/session';
// Libs
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
// Components
import { Bar, BarChart, Cell, LabelList, ReferenceLine, XAxis, YAxis } from 'recharts';
import { ChartContainer } from '../../../../../common-utils/components/ui/chart';
import Entity from '../../../../../common-utils/components/renders/entity/Entity';
// Common
import simCardFields from '../../fields/simCardFields';
import SIM_DATA_UNITS from '../../enums/SimDataUnits';
import genUID from '@/common-utils/functions/generateUniqueId';
// Lang
import { nsSimCard } from '@/app/i18n/namespaces';
import getTranslatedText from '@/common-utils/functions/getTranslatedText';

/**
 * @param root0
 * @param root0.data
 * @param root0.simOffer
 * @returns {JSX} RenderSimConsumptionChart
 */
function RenderSimConsumptionChart({ data, simOffer }) {
    // Store
    const { isSmallDevice } = useSessionStore(s => s.session) ?? {};

    // Lang
    const { i18n } = useTranslation();
    const lang = i18n.resolvedLanguage;
    const textSimOffer = simOffer.tariffData && simOffer.tariffDataUnit
        ? `${simOffer.tariffData} ${getTranslatedText(`units.${simOffer.tariffDataUnit}`, nsSimCard)}`
        : '';

    // Constants
    const dataKey = 'dataConsumption';
    const xKey = 'monthYear';
    const chartConfig = {
        [dataKey]: {
            label: dataKey,
            color: '#129121'
        }
    };

    // Functions
    const getSortedData = () => {
        const dataCopy = [...data];
        const slicedData = isSmallDevice
            ? dataCopy.slice(Math.max(dataCopy.length - 3, 0))
            : dataCopy;

        return slicedData
            .sort((a, b) => new Date(a.monthYear) - new Date(b.monthYear))
            .map(item => ({
                ...item,
                key: genUID()
            }));
    };

    const convertOfferToByte = () => {
        const { tariffData, tariffDataUnit } = simOffer;
        const multipliers = {
            KO: SIM_DATA_UNITS.KB,
            KB: SIM_DATA_UNITS.KB,
            MO: SIM_DATA_UNITS.MB,
            MB: SIM_DATA_UNITS.MB,
            GO: SIM_DATA_UNITS.GB,
            GB: SIM_DATA_UNITS.GB
        };

        const factor = multipliers[tariffDataUnit] || 1;
        const offerInByte = tariffData ? tariffData * factor : 0;

        return offerInByte;
    };

    const getDataConsumption = (dataUsage) => {
        let entity;

        if (dataUsage < SIM_DATA_UNITS.KB) {
            entity = <Entity
                className='mb-4'
                entity='units.data.byte'
                data={{ dataUsage }}
            />;
        }
        else if (dataUsage < SIM_DATA_UNITS.MB) {
            const dataKB = dataUsage / SIM_DATA_UNITS.KB;
            entity = <Entity
                className='mb-8'
                entity='units.data.k_byte'
                data={{ dataUsage: dataKB.toFixed(2) }}
            />;
        }
        else if (dataUsage < SIM_DATA_UNITS.GB) {
            const dataMB = dataUsage / SIM_DATA_UNITS.MB;
            entity = (
                <Entity
                    className='mb-4'
                    entity='units.data.m_byte'
                    data={{ dataUsage: dataMB.toFixed(2) }}
                />
            );
        }
        else {
            const dataGB = dataUsage / SIM_DATA_UNITS.GB;
            entity = <Entity
                className='mb-4'
                entity='units.data.g_byte'
                data={{ dataUsage: dataGB.toFixed(2) }}
            />;
        }
        return entity;
    };
    const calculatedMargin = () => {
        return textSimOffer.length * 8;
    };

    return (
        <div className='grid grid-cols-1 xl:grid-cols-[1fr_5fr] gap-4 py-4'>
            <div>
                <Entity entity={simCardFields.SIM_CARD.DATA_USAGE.entity}
                    namespace={nsSimCard}
                    className='font-bold'
                />
            </div>
            <div className='w-full overflow-x-auto'>
                <ChartContainer
                    config={chartConfig}
                    className='min-h-[250px] sm:min-h-[300px]'
                    style={{
                        width: `${Math.max(getSortedData().length * 60, 400)}px`,
                        height: '100%'
                    }}
                >
                    <BarChart
                        accessibilityLayer
                        data={getSortedData()}
                        margin={{ left: calculatedMargin() }}
                    >
                        <defs>
                            {getSortedData().map((entry, index) => {
                                const usage = entry[dataKey];
                                const offer = convertOfferToByte();
                                // Percentage of bar filled under the offer
                                const percent = usage > 0 ? Math.min((offer / usage) * 100, 100) : 100;

                                return (
                                    <linearGradient
                                        id={`bar-gradient-${index}`}
                                        key={entry.key}
                                        x1='0' y1='1' x2='0' y2='0'
                                    >
                                        {/* Value under the threshold */}
                                        <stop offset='0%' stopColor='#129121' />
                                        <stop offset={`${percent}%`} stopColor='#129121' />
                                        {/* Value over the threshold */}
                                        <stop offset={`${percent + 0.1}%`} stopColor='#d9534f' />
                                        <stop offset='100%' stopColor='#d9534f' />
                                    </linearGradient>
                                );
                            })}
                        </defs>
                        <XAxis
                            dataKey={xKey}
                            axisLine={false}
                            tickLine={false}
                            interval={0}
                            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: '10px' }}
                            tickFormatter={(val) => DateTime.fromISO(val, { locale: lang }).toFormat('MMM yy')}
                        />
                        <YAxis
                            axisLine={true}
                            tickLine={false}
                            tick={false}
                            width={5}
                            domain={[0, (dataMax) => Math.max(dataMax * 1.2, convertOfferToByte() * 1.2, 1)]}
                        />
                        <ReferenceLine
                            label={{ value: textSimOffer, fill: 'black', position: 'left' }}
                            y={convertOfferToByte()}
                            stroke='#d9534f'
                            strokeDasharray='5 5'
                        />
                        <Bar
                            dataKey={dataKey}
                            radius={4}
                            barSize={48}
                            domain={[0, (dataMax) => dataMax * 1.1]}
                        >
                            {getSortedData().map((item, index) => (
                                <Cell
                                    key={`${item.key}-${index}`}
                                    fill={`url(#bar-gradient-${index})`}
                                />
                            ))}
                            <LabelList
                                dataKey={dataKey}
                                position='top'
                                content={({ x, y, width, value }) => {
                                    const offsetY = Math.max(y - 32, 0);
                                    return (
                                        <foreignObject x={x} y={offsetY} width={width} height={32}>
                                            <div className='flex items-center justify-center'>
                                                {getDataConsumption(value)}
                                            </div>
                                        </foreignObject>
                                    );
                                }}
                            />
                        </Bar>
                    </BarChart>
                </ChartContainer>
            </div>
        </div>
    );
}

RenderSimConsumptionChart.propTypes = {
    data: PropTypes.array.isRequired,
    simOffer: PropTypes.object.isRequired
};

export default RenderSimConsumptionChart;